import type { ActiveUser, LoginStatistic, RecentLogin } from './types';

export function formatBytes(bytes: number | null | undefined): string {
  if (!bytes) return '0 B';
  const k = 1024;
  const sizes = ['B', 'KB', 'MB', 'GB', 'TB'];
  const i = Math.floor(Math.log(bytes) / Math.log(k));
  return `${Math.round((bytes / Math.pow(k, i)) * 100) / 100} ${sizes[i]}`;
}

export function formatDate(dateString: string | null | undefined): string {
  if (!dateString) return '—';
  try {
    return new Date(dateString).toLocaleString();
  } catch {
    return dateString;
  }
}

export function getTimeAgo(dateString: string | null | undefined): string {
  if (!dateString) return 'Never';
  const date = new Date(dateString);
  const now = new Date();
  const diffMs = now.getTime() - date.getTime();
  const diffMins = Math.floor(diffMs / 60000);
  const diffHours = Math.floor(diffMs / 3600000);
  const diffDays = Math.floor(diffMs / 86400000);

  if (diffMins < 1) return 'Just now';
  if (diffMins < 60) return `${diffMins} minute${diffMins > 1 ? 's' : ''} ago`;
  if (diffHours < 24) return `${diffHours} hour${diffHours > 1 ? 's' : ''} ago`;
  return `${diffDays} day${diffDays > 1 ? 's' : ''} ago`;
}

export function getRoleBadgeColor(role: string | null | undefined, isDark = false): string {
  if (isDark) {
    if (role?.includes('RETAIL') || role?.includes('MARKETPLACE')) {
      return 'bg-blue-900/40 text-blue-300 border border-blue-700/50';
    }
    if (role?.includes('BUSINESS')) {
      return 'bg-green-900/40 text-green-300 border border-green-700/50';
    }
    if (role?.includes('ADMIN') || role?.includes('SUPER')) {
      return 'bg-purple-900/40 text-purple-300 border border-purple-700/50';
    }
    return 'bg-gray-800/40 text-gray-300 border border-gray-700/50';
  }
  if (role?.includes('RETAIL') || role?.includes('MARKETPLACE')) {
    return 'bg-blue-100 text-blue-800';
  }
  if (role?.includes('BUSINESS')) {
    return 'bg-green-100 text-green-800';
  }
  if (role?.includes('ADMIN') || role?.includes('SUPER')) {
    return 'bg-purple-100 text-purple-800';
  }
  return 'bg-gray-100 text-gray-800';
}

export function getResourceBarColor(
  usage: number,
  warningThreshold = 60,
  criticalThreshold = 80
): string {
  if (usage > criticalThreshold) return 'bg-red-500';
  if (usage > warningThreshold) return 'bg-yellow-500';
  return 'bg-green-500';
}

export function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === 'object' && value !== null;
}

/**
 * Walk `err.response.data` safely and return it as a `Record<string, unknown>`
 * when present. Callers can then read specific fields using narrow guards
 * rather than hand-rolled `as { response?: ... }` chains.
 */
export function getApiResponseData(err: unknown): Record<string, unknown> | undefined {
  if (!isRecord(err)) return undefined;
  const response = isRecord(err.response) ? err.response : undefined;
  if (!response) return undefined;
  return isRecord(response.data) ? response.data : undefined;
}

/**
 * Extract the HTTP status from an Axios-like error, or `undefined` if none.
 * Safe on any thrown value.
 */
export function getApiStatus(err: unknown): number | undefined {
  if (!isRecord(err)) return undefined;
  const response = isRecord(err.response) ? err.response : undefined;
  const status = response?.status;
  return typeof status === 'number' ? status : undefined;
}

/**
 * True if `err` looks like an Axios error whose HTTP status matches `status`.
 * Useful for 401/403/404/409 branching inside catch blocks.
 */
export function isAxiosStatus(err: unknown, status: number): boolean {
  return getApiStatus(err) === status;
}

/**
 * Extract the error `code` string (network errors, Axios error codes, etc.)
 * from an unknown thrown value.
 */
export function getApiErrorCode(err: unknown): string | undefined {
  if (!isRecord(err)) return undefined;
  return typeof err.code === 'string' ? err.code : undefined;
}

/**
 * Extract the server-provided structured error payload `{ message, code }`
 * from an API response body. Falls back to the top-level `err.message` for
 * non-HTTP errors. Returns `undefined` when no useful detail is available.
 *
 * Use this when the UI wants to show both the message and an error code
 * (e.g. connection/test result panels). For plain message extraction,
 * prefer `extractApiError`.
 */
export function getApiErrorDetail(
  err: unknown
): { message?: string; code?: string } | undefined {
  if (!isRecord(err)) return undefined;
  const response = isRecord(err.response) ? err.response : undefined;
  const data = response && isRecord(response.data) ? response.data : undefined;
  const errorData = data?.error;

  if (isRecord(errorData)) {
    const detail: { message?: string; code?: string } = {};
    if (typeof errorData.message === 'string') detail.message = errorData.message;
    if (typeof errorData.code === 'string') detail.code = errorData.code;
    return Object.keys(detail).length > 0 ? detail : undefined;
  }
  if (typeof errorData === 'string' && errorData.length > 0) {
    return { message: errorData };
  }
  if (typeof err.message === 'string' && err.message.length > 0) {
    return { message: err.message };
  }
  return undefined;
}

/**
 * Resolve a human-readable message from an unknown thrown value, preferring a
 * server-provided API error, then `userMessage` (set by the shared axios
 * interceptor for network/timeout failures, where `err.message` is only the
 * opaque "Network Error"), then `err.message`, then `fallback`.
 */
export function extractApiError(
  err: unknown,
  fallback: string = 'An unexpected error occurred'
): string {
  if (isRecord(err)) {
    const response = isRecord(err.response) ? err.response : undefined;
    const data = response && isRecord(response.data) ? response.data : undefined;

    if (data) {
      const errorData = data.error;
      if (isRecord(errorData)) {
        if (typeof errorData.message === 'string') return errorData.message;
        return JSON.stringify(errorData);
      }
      if (typeof errorData === 'string' && errorData.length > 0) return errorData;

      if (typeof data.message === 'string' && data.message.length > 0) return data.message;
    }

    if (typeof err.userMessage === 'string' && err.userMessage.length > 0) return err.userMessage;

    if (typeof err.message === 'string' && err.message.length > 0) return err.message;
  }

  return fallback;
}

/**
 * Unwraps a JSend-style API response. Returns the inner `data.data`
 * when present, otherwise the raw `data`, otherwise the provided fallback
 * (defaults to an empty object cast as `T`).
 *
 * The generic `T` is unchecked at runtime — callers opt into a shape they
 * trust the server to return.
 */
export function unwrapApiPayload<T = Record<string, unknown>>(
  response: { data?: unknown } | null | undefined,
  fallback?: T
): T {
  const body = response?.data;
  if (body && typeof body === 'object' && 'data' in (body as Record<string, unknown>)) {
    const inner = (body as { data?: unknown }).data;
    if (inner !== undefined && inner !== null) return inner as T;
  }
  if (body !== undefined && body !== null) return body as T;
  return fallback !== undefined ? fallback : ({} as T);
}

/**
 * Return the raw `err.response.data` as `unknown` without narrowing.
 * Useful for logging or when the caller wants to inspect the full payload
 * before picking fields.
 */
export function getApiResponseDataRaw(err: unknown): unknown {
  if (!isRecord(err)) return undefined;
  const response = isRecord(err.response) ? err.response : undefined;
  return response?.data;
}

interface UserLookupEntry {
  email?: string;
  firstName?: string;
  lastName?: string;
  first_name?: string;
  last_name?: string;
}

interface UserArrayEntry {
  id: number | string;
  email?: string;
  firstName?: string;
  lastName?: string;
  first_name?: string;
  last_name?: string;
}

export function resolveUserDisplay(
  userId: number | string | null | undefined,
  context: Record<string, unknown>,
  properties: Record<string, unknown>,
  lookupSources: {
    userLookup?: Record<string | number, UserLookupEntry>;
    allUsers?: UserArrayEntry[];
    loginStatistics?: LoginStatistic[];
    activeUsers?: ActiveUser[];
    recentLogins?: RecentLogin[];
  }
): string {
  const role = (context.role || context.userRole || context.user_role ||
    properties.role || properties.userRole || properties.user_role) as string | undefined;

  if (!userId) {
    return role ? role.replace(/_/g, ' ') : 'Guest';
  }

  const userIdKey = String(userId);
  const userIdNum = typeof userId === 'string' ? parseInt(userId, 10) : userId;

  const contextEmail = (context.email || properties.email) as string | undefined;
  if (contextEmail) return contextEmail;

  if (lookupSources.userLookup) {
    const user = lookupSources.userLookup[userIdNum] ||
      lookupSources.userLookup[userIdKey] ||
      lookupSources.userLookup[userId];
    if (user) {
      return user.email || `${user.firstName || user.first_name || ''} ${user.lastName || user.last_name || ''}`.trim() || `User #${userId}`;
    }
  }

  const findInArray = <T extends UserArrayEntry>(arr: T[] | undefined): T | null => {
    if (!arr?.length) return null;
    return arr.find(u => {
      const uId = typeof u.id === 'string' ? parseInt(u.id, 10) : u.id;
      return uId === userIdNum || String(u.id) === userIdKey;
    }) ?? null;
  };

  const found: UserArrayEntry | null =
    findInArray(lookupSources.allUsers) ??
    findInArray(lookupSources.loginStatistics) ??
    findInArray(lookupSources.activeUsers) ??
    findInArray(lookupSources.recentLogins);

  if (found) {
    const firstName = found.firstName ?? found.first_name ?? '';
    const lastName = found.lastName ?? found.last_name ?? '';
    return found.email || `${firstName} ${lastName}`.trim() || `User #${userId}`;
  }

  return `User #${userId}`;
}

const ACCOUNT_ID_SALT = 'futuredev-account-id-v1';

function fnv1a32(input: string): number {
  let hash = 0x811c9dc5;
  for (let i = 0; i < input.length; i += 1) {
    hash ^= input.charCodeAt(i);
    hash = Math.imul(hash, 0x01000193);
  }
  return hash >>> 0;
}

/** Public account number. Prefers the stored column; otherwise hashes the internal id. */
export function formatAccountId(
  userId: string | number | null | undefined,
  stored?: string | null,
): string {
  const fromStore = typeof stored === 'string' ? stored.trim() : '';
  if (fromStore) return fromStore;
  if (userId == null || userId === '') return '—';
  const hex = fnv1a32(`${ACCOUNT_ID_SALT}:${userId}`).toString(16).padStart(8, '0').toUpperCase();
  return `IES-${hex.slice(0, 4)}-${hex.slice(4, 8)}`;
}
